import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

(() => {
  'use strict';

  if (window.AuraProductCardLoaded) return;
  window.AuraProductCardLoaded = true;

  const AURA_SOURCE = 'aura-product-card';

  const SUCCESS_TEXT = '✓ Added';
  const LOADING_TEXT = 'Adding…';
  const RETRY_TEXT = 'Retry';
  const COPIED_TEXT = 'Link copied';

  const SUCCESS_DELAY = 2200;
  const ERROR_DELAY = 2600;
  const SHARE_DELAY = 1600;

  const RESPONSIVE_WIDTHS = [480, 720, 960, 1200];

  const productCache = new Map();
  const variantImageMapCache = new WeakMap();
  const buttonTimerCache = new WeakMap();

  const getCard = (target) => target?.closest?.('[data-apc-card], .aura-product-card');

  const parseJSON = (text, fallback = {}) => {
    try {
      return JSON.parse(text || '{}');
    } catch (_error) {
      return fallback;
    }
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, options);
    const text = await response.text();
    const data = parseJSON(text, null);

    if (!response.ok) {
      const message = data?.description || data?.message || 'Request failed.';
      const error = new Error(message);
      error.response = response;
      error.data = data;
      throw error;
    }

    return data;
  };

  const fetchProductData = async (handle) => {
    if (!handle) return null;

    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const promise = fetchJSON(`/products/${encodeURIComponent(handle)}.js`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).catch((error) => {
      productCache.delete(handle);
      throw error;
    });

    productCache.set(handle, promise);
    return promise;
  };

  const buildSrcsetFromImageSrc = (src) => {
    if (!src) return '';

    return RESPONSIVE_WIDTHS
      .map((width) => {
        const separator = src.includes('?') ? '&' : '?';
        return `${src}${separator}width=${width} ${width}w`;
      })
      .join(', ');
  };

  const getVariantImageMap = (card) => {
    if (!card) return {};
    if (variantImageMapCache.has(card)) return variantImageMapCache.get(card);

    const script = card.querySelector('[data-apc-variant-images]');
    const parsed = script ? parseJSON(script.textContent, {}) : {};

    variantImageMapCache.set(card, parsed);
    return parsed;
  };

  const getModalByUid = (uid) => {
    if (!uid) return null;

    return Array.from(document.querySelectorAll('[data-apc-modal]')).find((modal) => {
      return modal.dataset.apcModal === String(uid);
    });
  };

  const buildVariantProductUrl = (card, variantId) => {
    if (!card || !variantId) return '';

    const baseUrl =
      card.dataset.productUrl ||
      card.querySelector('[data-apc-product-link]')?.getAttribute('href') ||
      card.querySelector('.aura-product-card__image-link')?.getAttribute('href') ||
      '';

    if (!baseUrl) return '';

    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('variant', String(variantId));

      return url.pathname + url.search + url.hash;
    } catch (_error) {
      const cleanBase = String(baseUrl).split('?')[0].split('#')[0];
      return `${cleanBase}?variant=${encodeURIComponent(String(variantId))}`;
    }
  };

  const updateProductLinks = (card, variantId) => {
    if (!card || !variantId) return;

    const variantUrl = buildVariantProductUrl(card, variantId);
    if (!variantUrl) return;

    card.dataset.selectedVariantUrl = variantUrl;

    card
      .querySelectorAll(
        [
          '[data-apc-product-link]',
          '.aura-product-card__image-link',
          '.aura-product-card__title a',
        ].join(',')
      )
      .forEach((link) => {
        link.href = variantUrl;
      });

    card.querySelectorAll('[data-apc-share], [data-share-url]').forEach((button) => {
      button.dataset.shareUrl = variantUrl;
    });

    const uid = card.dataset.apc;
    const modal = getModalByUid(uid);

    if (modal) {
      modal.querySelectorAll('[data-apc-product-link], .aura-product-card-modal__info a[href*="/products/"]').forEach((link) => {
        link.href = variantUrl;
      });
    }
  };

  const setPrimaryImage = (card, imageData) => {
    if (!card || !imageData?.src) return false;

    const primaryImage = card.querySelector('[data-apc-primary-image], .aura-product-card__image-primary');
    if (!primaryImage) return false;

    primaryImage.src = imageData.src;

    if (imageData.srcset) {
      primaryImage.srcset = imageData.srcset;
    } else {
      primaryImage.srcset = buildSrcsetFromImageSrc(imageData.src);
    }

    if (imageData.alt) {
      primaryImage.alt = imageData.alt;
    }

    card.dataset.apcSelectedVariantImage = 'true';
    return true;
  };

  const setModalImage = (card, imageData) => {
    if (!card || !imageData?.src) return;

    const uid = card.dataset.apc;
    const modal = getModalByUid(uid);
    if (!modal) return;

    const modalImage = modal.querySelector('.aura-product-card-modal__image img');
    if (!modalImage) return;

    modalImage.src = imageData.src;

    if (imageData.srcset) {
      modalImage.srcset = imageData.srcset;
    } else {
      modalImage.srcset = buildSrcsetFromImageSrc(imageData.src);
    }

    if (imageData.alt) {
      modalImage.alt = imageData.alt;
    }
  };

  const updateVariantTargets = (card, variantId) => {
    if (!card || !variantId) return;

    const cleanVariantId = String(variantId);
    card.dataset.variantId = cleanVariantId;

    updateProductLinks(card, cleanVariantId);

    card.querySelectorAll('[data-apc-add-to-cart]').forEach((button) => {
      button.dataset.variantId = cleanVariantId;
      button.dataset.apcVariantId = cleanVariantId;
    });

    card.querySelectorAll('[data-apc-buy-now]').forEach((link) => {
      link.dataset.variantId = cleanVariantId;
      link.dataset.apcVariantId = cleanVariantId;
      link.href = `/cart/${cleanVariantId}:1`;
    });

    card.querySelectorAll('input[name="id"][type="hidden"]').forEach((input) => {
      input.value = cleanVariantId;
    });

    card.querySelectorAll('select[name="id"]').forEach((select) => {
      const optionExists = Array.from(select.options).some((option) => option.value === cleanVariantId);
      if (optionExists) {
        select.value = cleanVariantId;
      }
    });

    card.querySelectorAll('input[name="id"][type="radio"]').forEach((radio) => {
      radio.checked = radio.value === cleanVariantId;
    });
  };

  const updateCardImageFromVariantId = (card, variantId) => {
    if (!card || !variantId) return false;

    const variantImages = getVariantImageMap(card);
    const variantImage = variantImages[String(variantId)];

    updateVariantTargets(card, variantId);

    if (!variantImage?.src) return false;

    setPrimaryImage(card, variantImage);
    setModalImage(card, variantImage);

    return true;
  };

  const getFallbackVariantImageFromProductData = async (card, variantId) => {
    if (!card || !variantId) return null;

    const handle = card.dataset.productHandle;
    if (!handle) return null;

    const productData = await fetchProductData(handle);
    if (!productData || !Array.isArray(productData.variants)) return null;

    const matchedVariant = productData.variants.find((variant) => String(variant.id) === String(variantId));

    if (!matchedVariant?.featured_image?.src) return null;

    return {
      src: matchedVariant.featured_image.src,
      srcset: buildSrcsetFromImageSrc(matchedVariant.featured_image.src),
      alt: matchedVariant.featured_image.alt || productData.title || '',
    };
  };

  const getCurrentVariantIdFromCard = (card) => {
    if (!card) return null;

    const checkedVariantInput = card.querySelector('input[name="id"]:checked');
    if (checkedVariantInput?.value) return checkedVariantInput.value;

    const variantSelect = card.querySelector('select[name="id"]');
    if (variantSelect?.value) return variantSelect.value;

    const hiddenVariantInput = card.querySelector('input[name="id"][type="hidden"]');
    if (hiddenVariantInput?.value) return hiddenVariantInput.value;

    if (card.dataset.variantId) return card.dataset.variantId;

    return null;
  };

  const getVariantIdFromSwatch = async (card, swatch) => {
    if (!card || !swatch) return null;

    const directVariantId =
      swatch.dataset.variantId ||
      swatch.dataset.apcVariantId ||
      swatch.closest('[data-variant-id]')?.dataset.variantId ||
      swatch.closest('[data-apc-variant-id]')?.dataset.apcVariantId;

    if (directVariantId) return String(directVariantId);

    const handle = card.dataset.productHandle;
    const optionIndex = Number.parseInt(swatch.dataset.optionIndex, 10);
    const value = swatch.dataset.value;

    if (!handle || Number.isNaN(optionIndex) || !value) return null;

    const productData = await fetchProductData(handle);
    if (!productData || !Array.isArray(productData.variants)) return null;

    const matchedVariant = productData.variants.find((variant) => {
      return variant.options?.[optionIndex] === value;
    });

    return matchedVariant?.id ? String(matchedVariant.id) : null;
  };

  const applyVariantSelection = async (card, variantId) => {
    if (!card || !variantId) return;

    const cleanVariantId = String(variantId);

    const updatedFromInlineMap = updateCardImageFromVariantId(card, cleanVariantId);
    if (updatedFromInlineMap) return;

    updateVariantTargets(card, cleanVariantId);

    const fallbackImage = await getFallbackVariantImageFromProductData(card, cleanVariantId);

    if (fallbackImage?.src) {
      setPrimaryImage(card, fallbackImage);
      setModalImage(card, fallbackImage);
    }
  };

  const activateSwatch = (swatch) => {
    const swatchWrapper = swatch.closest('.aura-product-card__swatches');
    if (!swatchWrapper) return;

    swatchWrapper.querySelectorAll('[data-apc-swatch]').forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-pressed', 'false');
    });

    swatch.classList.add('is-active');
    swatch.setAttribute('aria-pressed', 'true');
  };

  const handleSwatchClick = async (swatch) => {
    const card = getCard(swatch);
    if (!card) return;

    activateSwatch(swatch);

    const variantId = await getVariantIdFromSwatch(card, swatch);
    if (!variantId) return;

    await applyVariantSelection(card, variantId);
  };

  const bindMouseLeaveReset = (card) => {
    if (!card || card.dataset.apcMouseleaveBound === 'true') return;

    card.dataset.apcMouseleaveBound = 'true';

    card.addEventListener('mouseleave', () => {
      window.setTimeout(() => {
        card.dataset.apcSelectedVariantImage = 'false';
      }, 120);
    });
  };

  const normalizeSwatches = () => {
    document.querySelectorAll('[data-apc-swatch]').forEach((swatch) => {
      if (!swatch.hasAttribute('role')) {
        swatch.setAttribute('role', 'button');
      }

      if (!swatch.hasAttribute('tabindex')) {
        swatch.setAttribute('tabindex', '0');
      }

      if (!swatch.hasAttribute('aria-pressed')) {
        swatch.setAttribute('aria-pressed', swatch.classList.contains('is-active') ? 'true' : 'false');
      }
    });
  };

  const initCards = () => {
    document.querySelectorAll('[data-apc-card], .aura-product-card').forEach((card) => {
      bindMouseLeaveReset(card);

      const variantId = getCurrentVariantIdFromCard(card);
      if (variantId) {
        updateVariantTargets(card, variantId);
      }
    });

    normalizeSwatches();
  };

  const getCartSectionIds = () => {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      const sectionId = component.dataset.sectionId;
      if (sectionId) sectionIds.add(sectionId);
    });

    document.querySelectorAll('[data-section-id][data-cart-section]').forEach((component) => {
      const sectionId = component.dataset.sectionId;
      if (sectionId) sectionIds.add(sectionId);
    });

    return Array.from(sectionIds);
  };

  /**
   * Announces an add-to-cart to the rest of the theme.
   *
   * Horizon 4.x listens for the Shopify standard event `shopify:cart:lines-update`
   * (see cart-icon.js, cart-drawer.js, component-cart-items.js). The event is
   * dispatched BEFORE the network request with a pending promise, so the cart
   * drawer can decide to auto-open immediately; the promise is then resolved with
   * the real cart once the request lands. This mirrors assets/product-form.js.
   *
   * @param {HTMLElement} card - element to dispatch from (bubbles to document)
   * @param {number} variantId
   * @param {number} quantity
   * @returns {{resolve: Function, reject: Function}} deferred controls
   */
  const beginCartUpdate = (card, variantId, quantity) => {
    const deferred = CartLinesUpdateEvent.createPromise();

    card.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );

    return deferred;
  };

  /**
   * Resolves a pending cart update with the refreshed cart.
   * `detail.sections` lets cart-items-component morph in place instead of
   * re-fetching the whole section.
   */
  const resolveCartUpdate = (deferred, { card, cart, variantId, quantity, sections, didError = false }) => {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart?.items || [],
        source: AURA_SOURCE,
        sourceId: String(variantId || ''),
        itemCount: quantity,
        productId: card?.dataset?.productId,
        sections: sections || {},
        didError,
      },
    });
  };

  const dispatchCartError = (card, { message, errors, description, code = 'INVALID' }) => {
    (card || document.body).dispatchEvent(
      new CartErrorEvent({
        error: message || 'Unable to add item.',
        code,
        detail: {
          source: AURA_SOURCE,
          description,
          errors,
        },
      })
    );
  };

  const fetchCart = () =>
    fetchJSON('/cart.js', {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

  const getQuantity = (card) => {
    const input = card?.querySelector('.aura-product-card__quantity-input');
    const parsed = Number.parseInt(input?.value, 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const normalizeQuantityInput = (input) => {
    if (!input) return;

    const parsed = Number.parseInt(input.value, 10);
    input.value = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const clearButtonTimer = (button) => {
    const timer = buttonTimerCache.get(button);
    if (timer) {
      window.clearTimeout(timer);
      buttonTimerCache.delete(button);
    }
  };

  const setButtonState = (button, state, text) => {
    if (!button) return;

    clearButtonTimer(button);

    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-success', state === 'success');
    button.classList.toggle('is-error', state === 'error');

    button.disabled = state === 'loading';
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

    if (text !== undefined) {
      button.textContent = text;
    }
  };

  const resetButtonLater = (button, text, delay) => {
    clearButtonTimer(button);

    const timer = window.setTimeout(() => {
      setButtonState(button, 'idle', text);
      buttonTimerCache.delete(button);
    }, delay);

    buttonTimerCache.set(button, timer);
  };

  const addToCart = async (button) => {
    const card = getCard(button);
    if (!card || button.disabled) return;

    const variantId = button.dataset.variantId || button.dataset.apcVariantId || card.dataset.variantId;
    const cleanVariantId = Number.parseInt(variantId, 10);

    if (!Number.isFinite(cleanVariantId) || cleanVariantId <= 0) {
      setButtonState(button, 'error', RETRY_TEXT);
      resetButtonLater(button, button.dataset.originalText || button.textContent.trim(), ERROR_DELAY);
      return;
    }

    const quantity = getQuantity(card);
    const originalText = button.dataset.originalText || button.textContent.trim();
    button.dataset.originalText = originalText;

    const sectionIds = getCartSectionIds();

    setButtonState(button, 'loading', LOADING_TEXT);

    // Dispatch before the request so the cart drawer can auto-open right away.
    const deferred = beginCartUpdate(card, cleanVariantId, quantity);

    try {
      const addPayload = {
        id: cleanVariantId,
        quantity,
      };

      if (sectionIds.length > 0) {
        addPayload.sections = sectionIds.join(',');
        addPayload.sections_url = window.location.pathname;
      }

      const addData = await fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(addPayload),
      });

      const cart = await fetchCart();

      resolveCartUpdate(deferred, {
        card,
        cart,
        variantId: cleanVariantId,
        quantity,
        sections: addData?.sections || {},
      });

      setButtonState(button, 'success', SUCCESS_TEXT);
      resetButtonLater(button, originalText, SUCCESS_DELAY);
    } catch (error) {
      const data = error?.data || {};
      const message = data.description || data.message || error?.message || 'Unable to add item.';
      const isApiError = Boolean(error?.response);

      dispatchCartError(card, {
        message,
        description: data.description,
        errors: data.errors,
        code: isApiError ? 'INVALID' : 'SERVICE_UNAVAILABLE',
      });

      // Settle the pending promise so listeners (drawer, cart icon, cart items)
      // don't hang. On an API error the cart may still have changed, so refresh
      // it and resolve with didError; on a network failure, reject.
      try {
        const cart = await fetchCart();
        resolveCartUpdate(deferred, {
          card,
          cart,
          variantId: cleanVariantId,
          quantity,
          didError: true,
        });
      } catch (refreshError) {
        deferred.reject(refreshError);
      }

      setButtonState(button, 'error', RETRY_TEXT);
      resetButtonLater(button, originalText, ERROR_DELAY);
    }
  };

  const openModal = (modal) => {
    if (!modal) return;

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const closeButton = modal.querySelector('[data-apc-modal-close]');
    if (closeButton) {
      window.setTimeout(() => closeButton.focus({ preventScroll: true }), 0);
    }
  };

  const closeModal = (modal) => {
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  const handleShare = async (button) => {
    if (!button) return;

    const originalText = button.dataset.originalText || button.textContent.trim();
    button.dataset.originalText = originalText;

    const shareUrl = button.dataset.shareUrl || '';
    const url = shareUrl.startsWith('http') ? shareUrl : window.location.origin + shareUrl;
    const title = button.dataset.shareTitle || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (_error) {
        return;
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);

        if (button.textContent.trim()) {
          button.textContent = COPIED_TEXT;
          resetButtonLater(button, originalText, SHARE_DELAY);
        }
      } catch (_error) {
        return;
      }
    }
  };

  document.addEventListener('click', async (event) => {
    const quantityButton = event.target.closest('[data-apc-quantity-action]');
    if (quantityButton) {
      const quantityBox = quantityButton.closest('.aura-product-card__quantity');
      const input = quantityBox?.querySelector('.aura-product-card__quantity-input');
      if (!input) return;

      const currentValue = Number.parseInt(input.value, 10) || 1;
      const action = quantityButton.dataset.apcQuantityAction;

      input.value = action === 'increase' ? currentValue + 1 : Math.max(1, currentValue - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const swatch = event.target.closest('[data-apc-swatch]');
    if (swatch) {
      event.preventDefault();
      await handleSwatchClick(swatch);
      return;
    }

    const atcButton = event.target.closest('[data-apc-add-to-cart]');
    if (atcButton) {
      event.preventDefault();
      await addToCart(atcButton);
      return;
    }

    const quickViewButton = event.target.closest('[data-apc-quick-view]');
    if (quickViewButton) {
      event.preventDefault();

      const uid = quickViewButton.dataset.apcQuickView;
      const modal = getModalByUid(uid);
      if (!modal) return;

      openModal(modal);
      return;
    }

    const modalClose = event.target.closest('[data-apc-modal-close]');
    if (modalClose) {
      closeModal(modalClose.closest('.aura-product-card-modal'));
      return;
    }

    const modalBg = event.target.closest('[data-apc-modal-bg]');
    if (modalBg) {
      closeModal(modalBg.closest('.aura-product-card-modal'));
      return;
    }

    const shareButton = event.target.closest('[data-apc-share]');
    if (shareButton) {
      event.preventDefault();
      await handleShare(shareButton);
    }
  });

  document.addEventListener('keydown', async (event) => {
    const swatch = event.target.closest?.('[data-apc-swatch]');

    if (swatch && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      await handleSwatchClick(swatch);
      return;
    }

    if (event.key !== 'Escape') return;

    document.querySelectorAll('.aura-product-card-modal.is-open').forEach((modal) => {
      closeModal(modal);
    });
  });

  document.addEventListener('change', async (event) => {
    const target = event.target;
    const card = getCard(target);

    if (target.matches?.('.aura-product-card__quantity-input')) {
      normalizeQuantityInput(target);
      return;
    }

    if (!card) return;

    if (target.matches('select[name="id"], input[name="id"]')) {
      await applyVariantSelection(card, target.value);
    }
  });

  document.addEventListener('input', (event) => {
    const target = event.target;

    if (target.matches?.('.aura-product-card__quantity-input')) {
      const parsed = Number.parseInt(target.value, 10);

      if (!Number.isFinite(parsed) || parsed < 1) {
        target.value = 1;
      }
    }
  });

  document.addEventListener('shopify:section:load', initCards);

  initCards();
})();